import { apiClient } from '@/hooks/useApi';

const DEVICE_ID_KEY = 'wl.deviceId';

function getClientDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;

    const id = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return '';
  }
}

export interface StaffLoginEntryDto {
  staffId: number;
  fullName: string;
  role: string;
  requiresPin: boolean;
  hasPassword: boolean;
}

export interface WorkspaceStaffEntry {
  staffId: number;
  fullName: string;
  role: string;
  hasPassword: boolean;
}

export interface WorkspaceStaffListResponse {
  companyName: string;
  slug: string;
  staff: WorkspaceStaffEntry[];
}

export interface StaffLoginResponse {
  staffId: number;
  fullName: string;
  role: string;
  token: string;
  expiresAt: string;
  mustChangePassword?: boolean;
}

export interface SoloCheckResponse {
  isSolo: boolean;
  autoRole?: string;
}

export interface LoginResponse {
  tenantId: string;
  slug: string;
  displayName: string;
  companyName: string;
  planName?: string;
  storageMode: string;
  canonicalHost?: string;
  // TenantApi serializes as camelCase: accessToken is the actual field name.
  accessToken?: string;
  // Legacy alias kept for any callers that pre-dated the field rename.
  token?: string;
  expiresAt: string;
  sessionId?: string;
  sessionLimit?: number;
  activeSessionCount?: number;
  passwordSet?: boolean;
  staffToken?: string;
  staffId?: number;
  staffRole?: string;
  staffFullName?: string;
  staffMustChangePassword?: boolean;
  portalHandoffCode?: string;
  billingStatus?: string;
  trialEndsOn?: string;
  subscriptionRenewalOn?: string;
  tenantTimezone?: string;
}

export interface ClientSessionDto {
  id: string;
  clientType: string;
  deviceInfo?: string;
  issuedAt: string;
  lastSeenAt: string;
  isActive: boolean;
  revokedAt?: string;
  revokedBy?: string;
}

export const authApi = {
  async login(slug: string, onboardingCode: string, clientType: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/api/auth/tenant-code-login', {
      slug,
      onboardingCode,
      platform: clientType,
      deviceId: getClientDeviceId(),
    });
    return data;
  },

  async googleLogin(idToken: string, clientType: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/api/auth/google-login', {
      idToken,
      clientType,
      deviceId: getClientDeviceId(),
    });
    return data;
  },

  async emailPasswordLogin(email: string, password: string, clientType: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/api/auth/email-password-login', {
      email,
      password,
      clientType,
      deviceId: getClientDeviceId(),
    });
    return data;
  },

  async ownerEmailPasswordLogin(email: string, password: string, clientType: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/api/auth/owner-email-password-login', {
      email,
      password,
      clientType,
      deviceId: getClientDeviceId(),
    });
    return data;
  },

  async tenantPasswordLogin(slug: string, email: string, password: string, clientType: string): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/api/auth/tenant-password-login', {
      slug,
      email,
      password,
      clientType,
      deviceId: getClientDeviceId(),
    });
    return data;
  },

  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/api/auth/forgot-password', { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post('/api/auth/reset-password', { token, newPassword });
  },

  async setPassword(newPassword: string): Promise<void> {
    await apiClient.post('/api/auth/set-password', { newPassword });
  },

  async heartbeat(): Promise<void> {
    await apiClient.post('/api/auth/session/heartbeat');
  },

  async listSessions(): Promise<ClientSessionDto[]> {
    const { data } = await apiClient.get<ClientSessionDto[]>('/api/auth/sessions');
    return data;
  },

  async revokeSession(sessionId: string): Promise<void> {
    await apiClient.post(`/api/auth/session/${sessionId}/revoke`);
  },

  async soloCheck(): Promise<SoloCheckResponse> {
    const { data } = await apiClient.get<SoloCheckResponse>('/api/staff/solo-check');
    return data;
  },

  async listStaffForLogin(): Promise<StaffLoginEntryDto[]> {
    const { data } = await apiClient.get<StaffLoginEntryDto[]>('/api/staff/list-for-login');
    return data;
  },

  async staffLogin(params: {
    username?: string;
    password?: string;
    staffId?: number;
    pin?: string;
  }): Promise<StaffLoginResponse> {
    const { data } = await apiClient.post<StaffLoginResponse>('/api/staff/login', params);
    return data;
  },

  async workspaceListStaff(slug: string): Promise<WorkspaceStaffListResponse> {
    const { data } = await apiClient.get<WorkspaceStaffListResponse>('/api/auth/workspace-staff', { params: { slug } });
    return data;
  },

  async workspaceStaffLogin(params: {
    slug: string;
    username?: string;
    password?: string;
    staffId?: number;
    pin?: string;
  }): Promise<LoginResponse> {
    const { data } = await apiClient.post<LoginResponse>('/api/auth/workspace-staff-login', {
      ...params,
      deviceId: getClientDeviceId(),
    });
    return data;
  },

  async reAuthenticate(password: string): Promise<{ staffToken?: string; staffId?: number; staffName?: string }> {
    const { data } = await apiClient.post<{ staffToken?: string; staffId?: number; staffName?: string }>('/api/auth/re-authenticate', { password });
    return data;
  },

};
